import { wholeMoneyFormatter } from "helpers/formatCurrency";
import { fetch as router } from "helpers/router";
import request from "utils/request";
import retrieveShareReferral from "utils/ShareReferral";

export const stripShareSuffix = () => {
  if (!/\/share\/?$/.test(window.location.pathname)) {
    return;
  }
  const basePath = window.location.pathname.replace(/\/share\/?$/, "");
  const { search, hash } = window.location;
  window.history.replaceState(null, "", (basePath || "/") + search + hash);
};

export const buildShareUrl = (baseUrl) => {
  const { pds } = retrieveShareReferral();
  if (!pds) {
    return baseUrl;
  }
  const url = new URL(baseUrl);
  url.searchParams.set("pds", pds);
  return url.toString();
};

export const fetchSupporters = async (type, campaignId, fundraiserId, offset, limit) => {
  let supporters;

  const url = type === "campaign"
    ? `${router("api.v2.campaigns.supporters.index", { campaign: campaignId })}?limit=${limit}&offset=${offset}`
    : `${router("api.v2.fundraisers.supporters.index", { fundraiser: fundraiserId })}?limit=${limit}&offset=${offset}`;

  await fetch(request(url))
    .then((res) => {
      supporters = res.json();
    })
    .catch(() => {
      console.error("Unable to get supporters");
    });

  return supporters;
};

export const getEntity = (type, campaign, fundraiser) => {
  if (type === "campaign") {
    if (!campaign) {
      return {};
    }
    return {
      raised: wholeMoneyFormatter(campaign.collected.amount),
      target: wholeMoneyFormatter(campaign.target.amount),
      collectedTargetHidden: campaign.collectedTargetHidden,
      collectedTargetProgressPercentHidden: campaign.collectedTargetProgressPercentHidden,
      collectedTargetProgressPercent: campaign.collectedTargetProgressPercent,
      video: campaign?.video[0]?.publicUrl,
      title: campaign.title,
      content: campaign.content,
      ownerName: campaign.ownerName,
    };
  }
  if (type === "fundraiser") {
    if (!fundraiser) {
      return {};
    }

    const raised = wholeMoneyFormatter(fundraiser.collected.amount);
    const target = wholeMoneyFormatter(fundraiser.target.amount);
    return {
      raised,
      target,
      collectedTargetHidden: false,
      collectedTargetProgressPercentHidden: false,
      collectedTargetProgressPercent: (raised / target) * 100,
      video: fundraiser.video[0]?.publicUrl,
      title: fundraiser.fundraiserName,
      content: fundraiser.content,
      ownerName: fundraiser.fundraiserName,
      teamName: fundraiser.team?.title,
      teamSlug: fundraiser.team?.slug,
    };
  }
};

const fetchShares = async (campaign) => {
  const url = router("api.v2.campaigns.statistics.share", { campaign });

  return fetch(request(url))
    .then((response) => {
      if (!response.status === 200) {
        throw new Error(response.status);
      }
      return response;
    })
    .then((response) => response.json());
};

export const getShareItemForCampaign = (campaign, url) => ({
  url: getBaseShareUrlForCampaign(campaign, url),
  imageUrl: campaign.assets[0]?.publicUrl ?? null,
  campaignTitle: campaign.title,
  caption: getCaptionForCampaign(campaign),
  type: "campaign",
  emailSubject: "Have you heard about this?",
  emailBody: "Hi! I thought you might be interested in this campaign -",
});

const getCaptionForCampaign = (campaign) => {
  const captions = [
    "Be a lifeline for",
    "Stand with",
    "Show up for",
    "Help make this possible for",
    "Lend your support to",
    "Help carry",
    "Add your voice to",
    "Be there for",
    "Help see this through for",
    "Rally behind",
    "Help push this forward for",
    "Give what you can to",
    "Be part of what's behind",
    "Help turn things around for",
    "Join the people behind",
    "Help carry the weight for",
    "Put your support behind",
    "Help get this where it needs to go for",
    "Be one of the many standing behind",
    "Help make a difference for",
  ];

  try {
    const selectedIndex = uuidToIndex(campaign.id, captions.length - 1);

    if (!selectedIndex) {
      return captions[0];
    }

    return captions[selectedIndex];
  } catch {
    return captions[0];
  }
};

const uuidToIndex = (uuid, n) => {
  // Strip dashes, take the last 8 hex chars — well within the random portion
  // of a UUIDv7 (timestamp + version/variant bits are all in the front half).
  const hex = uuid.replace(/-/g, "");
  const tail = hex.slice(-8); // 32 bits of randomness
  const num = parseInt(tail, 16);
  return num % n;
};

const getBaseShareUrlForCampaign = (campaign, url) => {
  const baseShareUrl = new URL(url);
  baseShareUrl.searchParams.set("utm_campaign", "campaign_share");
  baseShareUrl.searchParams.set("utm_id", String(campaign.id));

  return baseShareUrl.toString();
};

export default fetchShares;
